import React, { useState, useCallback, useEffect, useMemo } from "react";
import {
  Modal,
  Box,
  Typography,
  TextField,
  Button,
  useTheme,
  useMediaQuery,
  Autocomplete, 
  CircularProgress,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";

import "../assets/stylesheets/modal.css";
import { VENUE_DATA } from "../assets/const/constant";
import { getCombinedUrlSearchParams } from "../utils/eventUtils";
import { VENUE_SELECTOR_CONST } from "../assets/const/venue-const";
import { constructUrl } from "../utils/utils";
import { useLocation, useNavigate } from "react-router-dom";

export const VenueModal = ({ open, onClose, error, className }) => {
  const [errorMessage, setErrorMessage] = useState(null);
  const [selectedVenue, setSelectedVenue] = useState(null); 
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [isLoading, setIsLoading] = useState(false); 
  const location = useLocation();
  const navigate = useNavigate();

   const venueNames = useMemo(() => {
    return Object.entries(VENUE_DATA)
      .filter(
        ([key, venue]) =>
          venue &&
          typeof venue.venue_name === "string" &&
          venue.venue_name.trim() !== "" &&
          venue.venue_name.toLowerCase() !== "default"
      )
      .map(([key, venue]) => ({
        key: key.toUpperCase(),
        name: venue.venue_name.trim(),
      }));
  }, []);

  const filterOptions = (options, { inputValue }) => {
    const lowercasedInput = inputValue.toLowerCase();
    return options.filter(
      (option) =>
        option.name.toLowerCase().includes(lowercasedInput) ||
        option.key.toLowerCase().includes(lowercasedInput)
    );
  };

  const handleClick = () => {
    setIsLoading(true); // Start loading

     if (!selectedVenue || !selectedVenue.trim()) {
      setErrorMessage(VENUE_SELECTOR_CONST.error_cta);
      setIsLoading(false);
      return;
    }

    const lowerCaseInput = selectedVenue.toLowerCase().trim();
    // Using .find() returns the first match found in VENUE_DATA.
    // Object.entries(VENUE_DATA) returns an array of [key, value] pairs.
    const venueEntry = Object.entries(VENUE_DATA).find(([key, venue]) => {
      if (typeof venue !== 'object' || venue === null) {
        return false;
      }

      // Case-insensitive comparison for both venue_id and venue_name
      const venueCodeMatch = key && lowerCaseInput === key.toLowerCase();
      const venueNameMatch = venue.venue_name && lowerCaseInput === venue.venue_name.toLowerCase();

      return venueCodeMatch || venueNameMatch;
    });

    if (venueEntry) {
      const [venueKey] = venueEntry;
      try {
        // Redirect to the signup page for the found venue, using its key
        const newPath = constructUrl('/signup-form', venueKey.toLowerCase(), location.search);
        navigate(newPath);
        onClose();
      } catch (err) {
        console.error(VENUE_SELECTOR_CONST.redirect_error, err);
        setErrorMessage(VENUE_SELECTOR_CONST.redirect_error);
      } finally {
        setIsLoading(false);
      }
    } else {
      setErrorMessage(VENUE_SELECTOR_CONST.invalid_venue);
      setIsLoading(false);
    }
  }
  return (
    <Modal
      open={open}
      onClose={onClose}
      aria-labelledby="login-modal-title"
      aria-describedby="login-modal-description"
      className={className}
    >
      <Box
        sx={{
          position: "absolute",
          top: "35%",
          left: "50%",
          width: isMobile ? "70%" : "30%",
          transform: "translate(-50%, -50%)",
          bgcolor: "background.paper",
          boxShadow: 24,
          p: 4, 
          borderRadius: 2,
          border: 0,
          textAlign: "center",
        }}
      >
        <Box 
          sx={{ 
            display: "flex", 
            justifyContent: "space-between", 
            alignItems: "center", 
          }}
        >
          <Typography 
            textAlign={isMobile ? "center" : "left"}
            fontWeight="bold" 
            mb={-1}
          >
            {VENUE_SELECTOR_CONST.cta}
          </Typography>
          {!isMobile && <CloseIcon sx={{ marginBottom: 1 }} onClick={onClose} style={{ cursor: 'pointer' }} />}
        </Box>
        <form onSubmit={(e) => { e.preventDefault(); handleClick(); }} >
          <Box
             sx={{
              display: "inline-flex",
              flexDirection: isMobile ? "column" : "row",
              justifyContent: "center",
              alignItems: "center",
              minHeight: "100px",
              width: "100%",
              marginTop: isMobile ? "auto" : "-10px"
            }}
          >
          <Autocomplete
            freeSolo
            disableClearable
            disablePortal // Prevents the dropdown from appearing in a separate portal
            id="venue-autocomplete"
            options={venueNames}
            getOptionLabel={(option) => option.name || ""}
            filterOptions={filterOptions}
            onInputChange={(event, newInputValue) => {
              setErrorMessage(null) 
              setSelectedVenue(newInputValue);
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder={VENUE_SELECTOR_CONST.placeholder}
                error={!!errorMessage}
                FormHelperTextProps={{
                  sx: {
                    marginLeft: 0, 
                  },
                }}
              />
            )}
             sx={{ width: isMobile ? '100%' : '65%' }} 
          />
          <Button
            sx={{ 
              margin: isMobile ? "14px 0 0 0" : '10px', 
              width: isMobile ? "100%" : '60%', 
              padding: "10px 0",
              ":hover": {
                backgroundColor: "#9b3122"
              } 
            }}
            variant="contained"
            type=""
            data-test-id="footer-avail-btn"
          >
            {isLoading ? <CircularProgress size={24} color="inherit" /> : VENUE_SELECTOR_CONST.signup_desktop}
          </Button>
          </Box>
        </form>
        { (errorMessage || error) && (
          <Typography
            error={errorMessage || error}
            sx={{
              fontSize: "14px",
              textAlign: "left",
              color: "#D42B1E",
              marginTop: isMobile ? "14px" : "0",
            }}
          >{errorMessage || error}</Typography>
        )}
      </Box>
    </Modal>
  );
};
